/**
 * Role-based access control types and helpers.
 *
 * Roles are strictly hierarchical — each higher role inherits all lower capabilities.
 * guest(0) → member(1) → ptr(2) → subscriber(3) → moderator(4) → admin(5)
 */

export type AppRole = 'guest' | 'member' | 'ptr' | 'subscriber' | 'moderator' | 'admin';

const ROLE_LEVELS: Record<AppRole, number> = {
  guest: 0,
  member: 1,
  ptr: 2,
  subscriber: 3,
  moderator: 4,
  admin: 5,
};

/** Check if `userRole` meets or exceeds `requiredRole` in the hierarchy. */
export const hasRole = (userRole: AppRole, requiredRole: AppRole): boolean =>
  ROLE_LEVELS[userRole] >= ROLE_LEVELS[requiredRole];

// --- Permission helpers ---

/** PTR+ removes watermark from share cards. */
export const canRemoveWatermark = (r: AppRole) => hasRole(r, 'ptr');

/** PTR+ unlocks gated hub page content. */
export const canUnlockContent = (r: AppRole) => hasRole(r, 'ptr');

/** Subscriber+ gets search bar, metric cards, event panel, overlay mode, clean exports. */
export const canUseProFeatures = (r: AppRole) => hasRole(r, 'subscriber');

/** Moderator+ can review corrections and manage FAQ answers. */
export const canModerate = (r: AppRole) => hasRole(r, 'moderator');

/** Admin only — full dashboard, user management. */
export const canAdmin = (r: AppRole) => hasRole(r, 'admin');

/** Guest and member see ads; subscriber+ does not. */
export const shouldShowAds = (r: AppRole) => !hasRole(r, 'subscriber');

// --- Obscured route prefixes ---

export const ADMIN_PREFIX = 'd9k2m';
export const MOD_PREFIX = 'r7x4p';
